import {
  Day,
  getVacationDays,
  HoursOff,
  Punch,
  selectedDayAtom,
  useCreatePastPunch,
  useCreatePunch,
  useSetIsSick,
  useSetVacations,
  workedHours
} from "@mc-intranet/shared";
import { Add } from "@mui/icons-material";
import { DateRange } from "@mui/lab";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Switch,
  Typography
} from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { useAtom, useAtomValue } from "jotai";
import moment from "moment";
import React, { ChangeEvent, useState } from "react";
import { toast } from "react-toastify";

import { authStorageAtom, daysByUserQueryVariablesSelector } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import Loading from "../ui/Loading";
import CreateHoursOff from "./hoursOff/Create";
import UpdateHoursOff from "./hoursOff/Update";
import LocationModal from "./punches/LocationModal";
import PastPunchModal from "./punches/PastPunchModal";
import PunchCard from "./punches/PunchCard";

interface Props {
  userId?: string;
  showProgramVacation: boolean;
}

let currentVacationStatus = false;

const EditDayView: React.FC<Props> = (props) => {
  const { showProgramVacation, userId } = props;
  const authStorage = useAtomValue(authStorageAtom);
  const { user, role } = authStorage;
  const { client } = useGraphQLClient();
  const variables = useAtomValue(daysByUserQueryVariablesSelector);

  const [currentDay, setCurrentDay] = useAtom(selectedDayAtom);

  const [vacationDates, setVacationDates] = useState<DateRange<Date>>([
    null,
    null
  ]);
  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [openPastPunchModal, setOpenPastPunchModal] = useState(false);

  const showConfirmVacationBtn = vacationDates[0] || vacationDates[1];

  const updateCurrentDay = (vacation: boolean) => {
    setCurrentDay((prev) => {
      return {
        ...prev,
        punches: vacation ? [] : (prev?.punches as Punch[]),
        hoursOff: vacation ? [] : (prev?.hoursOff as HoursOff[]),
        vacation: vacation,
        isSick: false
      } as Day;
    });
  };

  const {
    setVacations,
    options: { isLoading: setVacationsLoading }
  } = useSetVacations({
    day: currentDay,
    updateCurrentDay,
    client,
    variables
  });

  const _setVacations = (variables: { user: string; dates: any }) => {
    setVacations(variables, {
      onSuccess: () => {
        toast.success("Vacanze richieste con successo", {
          position: toast.POSITION.TOP_CENTER
        });
      },
      onError: (error: any) => {
        console.log("error ", error);
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    });
  };

  const onVacationChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    // console.log("vacation", checked);
    // salvo il valore richiesto per poi settarlo al results
    currentVacationStatus = checked;

    updateCurrentDay(currentVacationStatus);
    _setVacations({
      user: userId ? userId : (user?.documentId as string),
      dates: [currentDay?.date]
    });
  };

  const onSelectVacations = (date: DateRange<Date | null>) => {
    setVacationDates(date);
  };

  const confirmVacations = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    // console.log("vacationDates", vacationDates);
    const vacations = getVacationDays(
      moment(vacationDates[0]),
      moment(vacationDates[1])
    );

    // console.log("vacations ", vacations);
    _setVacations({
      user: userId ? userId : (user?.documentId as string),
      dates: vacations
    });
  };

  const isAdministrator = role === "admin";
  const isToday = moment().format("YYYY-MM-DD") === currentDay?.date;
  const isPastDay = moment(currentDay?.date).isBefore(moment(), "day");
  const usePastFlow = isPastDay && !isAdministrator;

  const canPunch = isToday || isAdministrator || isPastDay;

  const onAddPunch = () => {
    if (usePastFlow) {
      setOpenPastPunchModal(true);
    } else {
      setOpenLocationModal(true);
    }
  };

  const {
    createPunchMutation,
    options: { isLoading: creatingPunch }
  } = useCreatePunch({
    day: currentDay,
    setCurrentDay,
    client,
    variables
  });

  const {
    createPastPunchMutation,
    options: { isLoading: creatingPastPunch }
  } = useCreatePastPunch({
    day: currentDay,
    setCurrentDay,
    client,
    variables
  });

  const onLocationConfermation = (location: string) => {
    createPunchMutation(
      {
        dayId: currentDay?.documentId as string,
        in: moment().format("HH:mm:ss.SSS"),
        location
      },
      {
        onError: (error: any) => {
          console.log("error ", error);
          toast.error(error?.response?.error.message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
    setOpenLocationModal(false);
  };

  const onPastPunchConfirmation = (
    location: string,
    inTime?: string,
    outTime?: string
  ) => {
    createPastPunchMutation(
      {
        dayId: currentDay?.documentId as string,
        location,
        in: inTime,
        out: outTime
      }
    );
    setOpenPastPunchModal(false);
  };

  const showAddBtn = () => {
    const isEmpty =
      currentDay?.punches &&
      currentDay?.punches.length === 0 &&
      !currentDay?.vacation &&
      !currentDay?.isSick;
    const lastRecord =
      currentDay?.punches &&
      currentDay?.punches[currentDay?.punches.length - 1];
    const isLastRecordCompleted =
      lastRecord === undefined
        ? false
        : lastRecord?.in !== null && lastRecord?.out !== null;

    return (isEmpty || isLastRecordCompleted) && canPunch;
  };

  const { ore, minuti } = workedHours(currentDay);

  const {
    setIsSick,
    options: { isLoading: setIsSickLoading }
  } = useSetIsSick({
    client,
    variables
  });

  const onIsSickChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    // console.log("vacation", checked);
    // currentVacationStatus = true;
    setCurrentDay((prev) => {
      return {
        ...prev,
        punches: checked ? [] : (prev?.punches as Punch[]),
        hoursOff: checked ? [] : (prev?.hoursOff as HoursOff[]),
        vacation: false,
        isSick: checked
      } as Day;
    });

    setIsSick(
      {
        user: userId ? userId : (user?.documentId as string),
        date: currentDay?.date,
        isSick: checked
      },
      {
        onSuccess: (data) => {
          setCurrentDay(data?.setIsSick?.data as Day);
          toast.success("In malattia salvato!", {
            position: toast.POSITION.TOP_CENTER
          });
        },
        onError: (error: any) => {
          console.log("error ", error);
          toast.error(error?.response?.error.message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
  };

  console.log("currentDay: >>", currentDay);

  return (
    <Box
      className="today"
      bgcolor="background.default"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={2}
      width="100%"
      sx={{ borderRadius: 4, boxShadow: 10 }}
    >
      {!currentDay ? (
        <Loading />
      ) : (
        <Box className="today-wrapper">
          <Typography
            variant="h4"
            gutterBottom
            component="h4"
            textAlign="center"
          >
            {moment(currentDay.date).format("DD-MM-YYYY")}
          </Typography>
          {currentDay.punches?.map((punch) => {
            return (
              <PunchCard
                key={punch?.documentId}
                day={currentDay}
                punch={punch as Punch}
                setCurrentDay={setCurrentDay}
              />
            );
          })}
          {showAddBtn() && (
            <LoadingButton
              className="add-btn"
              variant="contained"
              startIcon={<Add />}
              fullWidth
              onClick={onAddPunch}
              loading={creatingPunch || creatingPastPunch}
              sx={{ mb: 3 }}
            >
              {usePastFlow ? "Richiedi timbratura" : "Nuovo ingresso"}
            </LoadingButton>
          )}
          {!canPunch && (
            <Typography className="warning" sx={{ mb: 3, maxWidth: 400 }}>
              {"Non è possibile aggiungere timbrature per giorni futuri."}
            </Typography>
          )}
          {currentDay &&
            (currentDay.punches?.length ?? 0) > 0 && (
              <Typography
                variant="h6"
                component="h6"
                className="worked-hours"
                sx={{ mb: 3 }}
              >
                Totale:
                <span>{` ${ore} ore${
                  minuti === 0 ? "" : " " + minuti + " min"
                }`}</span>
              </Typography>
            )}
          {currentDay &&
            !currentDay.vacation &&
            !currentDay.isSick &&
            currentDay.hoursOff &&
            currentDay.hoursOff?.length > 0 && (
              <Card
                className="time-wrapper"
                sx={{ bgcolor: "background.default", my: 2 }}
              >
                <CardContent>
                  <Typography variant="h5" component="h5">
                    Permessi
                  </Typography>
                  {currentDay.hoursOff?.map((hours) => {
                    return (
                      <UpdateHoursOff
                        key={hours?.documentId}
                        userId={userId ? userId : user?.documentId}
                        day={currentDay}
                        hoursOff={hours as HoursOff}
                        setCurrentDay={setCurrentDay}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            )}
          {currentDay &&
            !currentDay.vacation &&
            !currentDay.isSick && (
              <CreateHoursOff day={currentDay} setCurrentDay={setCurrentDay} />
            )}
          {openLocationModal && (
            <LocationModal
              visible={openLocationModal}
              location="ufficio"
              setVisibility={setOpenLocationModal}
              onLocationConfermation={onLocationConfermation}
            />
          )}
          {openPastPunchModal && (
            <PastPunchModal
              visible={openPastPunchModal}
              setVisibility={setOpenPastPunchModal}
              onConfirm={onPastPunchConfirmation}
            />
          )}
          <Divider sx={{ my: 1 }} />
          <Box className="vacation-wrapper">
            <span>Oggi in Ferie </span>
            <Switch
              className="vacation-switch"
              checked={currentDay.vacation ?? false}
              onChange={onVacationChange}
            />

            {showProgramVacation && (
              <Box className="program-vacations" my={3}>
                <Box className="label" my={2}>
                  Programma Ferie:
                </Box>
                <DateRangePicker
                  localeText={{ start: "Inizio", end: "Fine" }}
                  value={vacationDates}
                  onChange={onSelectVacations}
                  // renderInput={(startProps: any, endProps: any) => (
                  //   <React.Fragment>
                  //     <TextField {...startProps} />
                  //     <Box sx={{ mx: 2 }}>{">"}</Box>
                  //     <TextField {...endProps} />
                  //   </React.Fragment>
                  // )}
                />
                {showConfirmVacationBtn && (
                  <LoadingButton
                    variant="outlined"
                    fullWidth
                    onClick={confirmVacations}
                    loading={setVacationsLoading}
                    disabled={setVacationsLoading}
                    sx={{ mt: 2 }}
                  >
                    Conferma date ferie
                  </LoadingButton>
                )}
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <div className="vacation-wrapper">
            <span>In Malattia</span>
            <Switch
              className="vacation-switch"
              checked={currentDay.isSick ?? false}
              onChange={onIsSickChange}
              disabled={setIsSickLoading}
            />
          </div>
        </Box>
      )}
    </Box>
  );
};

export default EditDayView;
